import { Input } from 'antd';
import React, { FC } from 'react';
import { $Formutil, connect } from 'react-antd-formutil';
import { BankCardItemTypes, BankCardSourceType } from 'components/BankCardSelect/type';
import FormItem from 'components/FormItem';
import FormLayout from 'components/FormLayout';
import Telcode from 'components/forms/Telcode';
import APP from 'stores/APP';
import security from 'stores/security';
import { printf } from 'utils/i18n';
import BankAccountMainType from '../BankAccountMainType';
import './style.scss';

export interface BankCardFormProps {
    bankInfo?: BankCardItemTypes;
    source?: BankCardSourceType;
    $formutil: $Formutil<BankCardFormItemType>;
}

export type BankCardFormItemType = Pick<
    BankCardItemTypes,
    | 'bankCountry'
    | 'bankName'
    | 'swiftCode'
    | 'clearingCode'
    | 'bankAccount'
    | 'holderName'
    | 'bankAccountMainType'
    | 'holderCountry'
    | 'holderCityState'
>;

const filterInvalidText = (text: string) => text.replace(/[^0-9a-zA-Z\s]/g, '');

const clearingCodeMap = {
    HK: {
        label: __('香港 Bank Code'),
        limit: 3,
        limitTips: __('香港bank code应该是3位数字')
    },
    AU: {
        label: __('澳大利亚 BSB'),
        limit: 6,
        limitTips: __('澳洲地区跨境汇款BSB号码应当是6位数字')
    },
    US: {
        label: __('ABA Routing Number'),
        limit: 9,
        limitTips: __('美国地区跨境汇款ABA号码应当是9位数字')
    },
    CA: {
        label: __('加拿大 Routing No.'),
        limit: 9,
        limitTips: __('加拿大Routing号码应当是9位数字')
    },
    ZA: {
        label: __('南非 Sort Code'),
        limit: 6,
        limitTips: __('南非地区跨境汇款Sort code应当是6位数字')
    }
};

const BankCardForm: FC<BankCardFormProps> = ({ bankInfo, source, $formutil }) => {
    const {
        bankName,
        bankCountry,
        swiftCode,
        clearingCode,
        bankAccount,
        holderName,
        bankAccountMainType,
        holderHomeAddr,
        holderCountry,
        holderCityState
    } = bankInfo || {};
    const { $params, $validate } = $formutil;
    const upperBankCountry = ($params.bankCountry || bankCountry || '')?.toUpperCase();
    // 是否需要展示swiftCode输入框
    const needSwiftCode =
        (!source || !['authentication', 'deposit_notice'].includes(source)) && upperBankCountry !== 'US';

    const {
        label: clearingCodeLabel,
        limit: clearingCodeLimit,
        limitTips: clearingCodeLimitTips
    } = clearingCodeMap[upperBankCountry] || {};

    return (
        <div className="bank-card-form">
            {/** ach出入金新增银行卡，固定bankCountry为美国 */}
            {source === 'authentication' ? (
                <FormItem
                    name="bankCountry"
                    label={__('Bank Country/Region')}
                    $defaultValue="US"
                    $formatter={() => __('美国 US')}>
                    <Input disabled />
                </FormItem>
            ) : (
                <>
                    <label className="d-block address-label">{__('Bank Country/Region')}</label>
                    <Telcode
                        name="bankCountry"
                        type="bankCountry"
                        className="country-select comp-form-item"
                        defaultTelcode={bankCountry || 'US'}
                        onChange={() => {
                            !!clearingCodeLabel && $validate('clearingCode');
                            needSwiftCode && $validate('swiftCode');
                        }}
                    />
                </>
            )}
            <FormItem
                label={__('Bank Name')}
                name="bankName"
                $defaultValue={bankName}
                $parser={(viewValue, $setViewValue) => $setViewValue(filterInvalidText(viewValue))}
                checker={value => !!value || __('Please enter Bank Name')}>
                <Input placeholder={__('Please enter Bank Name')} />
            </FormItem>
            {needSwiftCode && (
                <FormItem
                    label={__('SWIFT Code')}
                    name="swiftCode"
                    $defaultValue={swiftCode}
                    $parser={(viewValue, $setViewValue) => $setViewValue(viewValue.replace(/[^0-9a-zA-Z]/g, ''))}
                    checker={value => {
                        if (!value) {
                            return __('Please enter SWIFT Code');
                        }

                        if (value && !/^[a-zA-Z]{6}/.test(value)) {
                            return __('Swift code前6位必须是字母。');
                        }

                        if (value.length !== 8 && value.length !== 11) {
                            return __('SWIFT Code must be 8 or 11 digits.');
                        }

                        if (value.substr(4, 2).toUpperCase() !== upperBankCountry) {
                            return __('The 5-6th letters should be the same as the Bank Country/Region code');
                        }

                        return true;
                    }}>
                    <Input placeholder={__('Please enter SWIFT Code')} />
                </FormItem>
            )}
            {!!clearingCodeLabel && (
                <FormItem
                    label={clearingCodeLabel}
                    name="clearingCode"
                    $parser={(viewValue, $setViewValue) => $setViewValue(viewValue.replace(/[^0-9]/g, ''))}
                    $defaultValue={clearingCode}
                    checker={value => {
                        if (!value) {
                            return printf(__('Please enter %s'), clearingCodeLabel);
                        }

                        if (clearingCodeLimit && value.length !== clearingCodeLimit) {
                            return clearingCodeLimitTips;
                        }

                        return true;
                    }}>
                    <Input placeholder={printf(__('Please enter %s'), clearingCodeLabel)} />
                </FormItem>
            )}
            <FormItem
                label={__('Bank Account Number')}
                name="bankAccount"
                $defaultValue={bankAccount}
                $parser={(viewValue, $setViewValue) => $setViewValue(viewValue.replace(/[^0-9]/g, ''))}
                checker={value => {
                    if (!value) {
                        return __('Please enter Bank Account Number');
                    }

                    if (/^[0-9]{1,17}$/.test(value)) {
                        return true;
                    }

                    return __('Account Number could not exceed 17 digits.');
                }}>
                <Input placeholder={__('Please enter Bank Account Number')} disabled={!!bankAccount} />
            </FormItem>
            <FormItem
                label={__('Account Holder')}
                name="holderName"
                $defaultValue={holderName || security.webAccountInfo?.real_name_en}
                $parser={(viewValue, $setViewValue) => $setViewValue(filterInvalidText(viewValue))}
                checker={value => !!value || __('Please enter Account Holder')}>
                <Input
                    placeholder={__('Please enter Account Holder')}
                    disabled={!!(holderName || security.webAccountInfo?.real_name_en)}
                />
            </FormItem>
            {source === 'authentication' && <BankAccountMainType defaultValue={bankAccountMainType} />}
            {(!source || !['authentication', 'deposit_notice'].includes(source)) && (
                <>
                    <div className="address-box">
                        <label className="address-label">{__('Account Holder Address')}</label>
                        <div>
                            <FormLayout layout={APP.isMobile ? '1:1' : '1:2'} className="holder-info">
                                <Telcode
                                    name="holderCountry"
                                    type="bankCountry"
                                    className="country-select comp-form-item"
                                    defaultTelcode={holderCountry || 'US'}
                                />
                                <FormItem
                                    name="holderCityState"
                                    $defaultValue={holderCityState}
                                    $parser={(viewValue, $setViewValue) => $setViewValue(filterInvalidText(viewValue))}
                                    checker={value => {
                                        if (!value) {
                                            return __('Please enter City/ State');
                                        }

                                        if (value.length > 60) {
                                            return __('城市长度最长为60位字符');
                                        }

                                        return true;
                                    }}>
                                    <Input placeholder={__('Please enter City/ State')} />
                                </FormItem>
                            </FormLayout>
                        </div>
                    </div>
                    <FormItem
                        name="holderHomeAddr"
                        $defaultValue={holderHomeAddr}
                        $parser={(viewValue, $setViewValue) => $setViewValue(filterInvalidText(viewValue))}
                        checker={value => {
                            if (!value) {
                                return __('Please enter Street Address');
                            }

                            if (value.length > 240) {
                                return __('联系地址长度最长为240位字符');
                            }

                            return true;
                        }}>
                        <Input placeholder={__('Please enter Street Address')} />
                    </FormItem>
                </>
            )}
        </div>
    );
};

export default connect(BankCardForm);
